import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { TaskStatus } from '@/types';

export interface OptionsFilter {
    statuses: TaskStatus[];
    deadline: number | null;
    categories: number[];
    roomIds: number[];
    tagIds: number[];
    showExpired: boolean;
}

const initial: OptionsFilter = {
    statuses: ['Default', 'Reviewed', 'Selected'],
    deadline: null,
    categories: [],
    roomIds: [],
    tagIds: [],
    showExpired: true,
};

type Action =
    | { type: 'setStatuses'; statuses: TaskStatus[] }
    | { type: 'setDeadline'; deadline: number | null }
    | { type: 'setCategories'; categories: number[] }
    | { type: 'setRoomIds'; roomIds: number[] }
    | { type: 'setTagIds'; tagIds: number[] }
    | { type: 'setShowExpired'; showExpired: boolean };

function reducer(state: OptionsFilter, action: Action): OptionsFilter {
    switch (action.type) {
        case 'setStatuses':
            return { ...state, statuses: action.statuses };
        case 'setDeadline':
            return { ...state, deadline: action.deadline };
        case 'setCategories':
            return { ...state, categories: action.categories };
        case 'setRoomIds':
            return { ...state, roomIds: action.roomIds };
        case 'setTagIds':
            return { ...state, tagIds: action.tagIds };
        case 'setShowExpired':
            return { ...state, showExpired: action.showExpired };
    }
}

export interface OptionsFilterApi {
    filter: OptionsFilter;
    setStatuses: (statuses: TaskStatus[]) => void;
    setDeadline: (deadline: number | null) => void;
    setCategories: (categories: number[]) => void;
    setRoomIds: (roomIds: number[]) => void;
    setTagIds: (tagIds: number[]) => void;
    setShowExpired: (showExpired: boolean) => void;
}

const OptionsFilterContext = createContext<OptionsFilterApi | null>(null);

/**
 * Owns the buyer-options filter state for the duration of the configurator
 * session. Lives at the entry component so the state survives navigation
 * between /options and /submit/:projectDeadlineId.
 */
export const OptionsFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [filter, dispatch] = useReducer(reducer, initial);
    const api = useMemo<OptionsFilterApi>(
        () => ({
            filter,
            setStatuses: (statuses) => dispatch({ type: 'setStatuses', statuses }),
            setDeadline: (deadline) => dispatch({ type: 'setDeadline', deadline }),
            setCategories: (categories) => dispatch({ type: 'setCategories', categories }),
            setRoomIds: (roomIds) => dispatch({ type: 'setRoomIds', roomIds }),
            setTagIds: (tagIds) => dispatch({ type: 'setTagIds', tagIds }),
            setShowExpired: (showExpired) => dispatch({ type: 'setShowExpired', showExpired }),
        }),
        [filter]
    );
    return <OptionsFilterContext.Provider value={api}>{children}</OptionsFilterContext.Provider>;
};

export function useOptionsFilter(): OptionsFilterApi {
    const ctx = useContext(OptionsFilterContext);
    if (!ctx) {
        throw new Error('useOptionsFilter must be used inside <OptionsFilterProvider>');
    }
    return ctx;
}
